import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Container,
  Avatar,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { androidColors } from '../../theme/androidTheme';
import PageTitle from '../../components/PageTitle/PageTitle';

interface CryptoData {
  id: string;
  symbol: string;
  name: string;
  amount: number;
  price_buy: number;
  price_current: number;
  value_current: number;
  value_initial: number;
  gain_perte: number;
  gain_perte_pourcentage: number;
  icon?: string;
}

const EditCrypto: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { crypto, index } = location.state as { crypto: CryptoData; index: number };

  const [quantity, setQuantity] = useState(crypto.amount.toString());
  const [price, setPrice] = useState(crypto.price_buy.toString());
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cryptoIconUrl, setCryptoIconUrl] = useState<string | null>(null);

  useEffect(() => {
    loadCurrentPrice();
    loadCryptoIcon();
  }, []);

  const loadCurrentPrice = async () => {
    setIsPriceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://price-cryptonvx.mongo-price-storage.workers.dev/api/price/${crypto.symbol.toUpperCase()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const priceValue = data.price || data.current_price || 0;
        setCurrentPrice(priceValue);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du prix:', err);
    } finally {
      setIsPriceLoading(false);
    }
  };

  const loadCryptoIcon = async () => {
    try {
      const response = await fetch('https://cdn.cryptonvx.com/icons-data/index.json');
      if (response.ok) {
        const iconsData = await response.json();
        const cryptoData = iconsData[crypto.symbol.toUpperCase()];
        if (cryptoData && cryptoData.image_path) {
          setCryptoIconUrl(`https://cdn.cryptonvx.com/icons-data/${cryptoData.image_path}`);
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement de l\'icône:', err);
    }
  };

  const handleEditCrypto = async () => {
    setError(null);

    // Validation
    const quantityValue = parseFloat(quantity.replace(',', '.'));
    const priceValue = parseFloat(price.replace(',', '.'));

    if (!quantity || quantityValue <= 0) {
      setError(t('editCrypto.quantityPositive'));
      return;
    }
    if (!price || priceValue <= 0) {
      setError(t('editCrypto.pricePositive'));
      return;
    }

    setIsLoading(true);

    try {
      const tokenData = localStorage.getItem('token');
      if (!tokenData) {
        throw new Error(t('common.unauthenticated'));
      }

      let token;
      try {
        const parsedToken = JSON.parse(tokenData);
        token = parsedToken.token;
      } catch (e) {
        token = tokenData;
      }

      if (!token) {
        throw new Error(t('common.invalidToken'));
      }

      // 1. Récupérer le portefeuille actuel
      const getResponse = await fetch(`https://api.cryptonvx.com/v1/app?jwt=${token}&type=data`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!getResponse.ok) {
        if (getResponse.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        throw new Error(t('editCrypto.fetchPortfolioError'));
      }

      const portfolioData = await getResponse.json();
      
      // Initialiser les listes
      let pairs: string[] = [];
      let quantities: number[] = [];
      let bidPrices: number[] = [];

      // Si le portefeuille existe, récupérer les données
      if (Array.isArray(portfolioData) && portfolioData.length > 0) {
        const data = portfolioData[0];
        if (data.pairs && data.quantity && data.bid_price) {
          pairs = data.pairs.map((e: any) => e.toString());
          quantities = data.quantity.map((e: any) => parseFloat(e.toString()) || 0);
          bidPrices = data.bid_price.map((e: any) => parseFloat(e.toString()) || 0);
        }
      }

      // 2. Mettre à jour la crypto
      const index = pairs.indexOf(crypto.symbol);
      if (index !== -1) {
        quantities[index] = quantityValue;
        bidPrices[index] = priceValue;
      }

      // 3. Envoyer le portefeuille mis à jour
      const updateResponse = await fetch(`https://api.cryptonvx.com/v1/app?jwt=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          pairs,
          quantity: quantities,
          bid_price: bidPrices,
        }),
      });

      // En mode no-cors, on ne peut pas lire la réponse
      // On considère que la requête a réussi si on arrive ici
      // Succès - retourner à la page portfolio
      navigate('/portfolio', { state: { message: t('editCrypto.editSuccess'), type: 'success' } });
    } catch (err) {
      console.error('Erreur lors de la modification:', err);
      setError(err instanceof Error ? err.message : t('common.unknownError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const isFormValid = () => {
    const quantityValue = parseFloat(quantity.replace(',', '.'));
    const priceValue = parseFloat(price.replace(',', '.'));
    
    return quantityValue > 0 && priceValue > 0;
  };

  const renderCryptoIcon = () => {
    if (cryptoIconUrl) {
      return (
        <Avatar
          src={cryptoIconUrl}
          sx={{ width: 48, height: 48 }}
          onError={() => setCryptoIconUrl(null)}
        />
      );
    }
    
    return (
      <Avatar
        sx={{
          width: 48,
          height: 48,
          backgroundColor: `${androidColors.primaryGold}30`,
          color: androidColors.primaryGold,
          fontSize: '18px',
          fontWeight: 'bold',
        }}
      >
        {crypto.symbol[0]}
      </Avatar>
    );
  };

  return (
    <Box sx={{ backgroundColor: androidColors.darkBackground, minHeight: '100vh' }}>
      <PageTitle title={t('editCrypto.pageTitle', { symbol: crypto.symbol })} />
      <AppBar
        position="static"
        sx={{
          backgroundColor: androidColors.darkBackground,
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            sx={{ color: androidColors.textColor, mr: 2 }}
            onClick={handleBack}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              color: androidColors.textColor,
              fontWeight: 'bold',
            }}
          >
            Modifier {crypto.symbol.toUpperCase()}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container sx={{ py: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: androidColors.error,
              '& .MuiAlert-icon': {
                color: androidColors.error,
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* En-tête avec informations de la crypto */}
        <Card
          sx={{
            backgroundColor: androidColors.cardBackground,
            border: `1px solid ${androidColors.borderColor}`,
            borderRadius: '12px',
            mb: 3,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {renderCryptoIcon()}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: androidColors.textColor,
                    fontWeight: 'bold',
                  }}
                >
                  {crypto.symbol.toUpperCase()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: androidColors.textSecondary }}
                >
                  {crypto.name}
                </Typography>
              </Box>
              {isPriceLoading ? (
                <CircularProgress size={20} sx={{ color: androidColors.primaryGold }} />
              ) : currentPrice !== null ? (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: androidColors.textSecondary }}
                  >
                    {t('editCrypto.currentPrice')}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: androidColors.success,
                      fontWeight: 'bold',
                    }}
                  >
                    {currentPrice.toFixed(2)} €
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </CardContent>
        </Card>

        {/* Prix actuel détaillé */}
        {currentPrice !== null && (
          <Card
            sx={{
              backgroundColor: androidColors.cardBackground,
              border: `1px solid ${androidColors.borderColor}`,
              borderRadius: '12px',
              mb: 3,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: androidColors.success }} />
                <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                  {t('editCrypto.marketPrice')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: androidColors.success,
                    fontWeight: 'bold',
                  }}
                >
                  {currentPrice.toFixed(2)} €
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Formulaire de modification */}
        <Card
          sx={{
            backgroundColor: androidColors.cardBackground,
            border: `1px solid ${androidColors.borderColor}`,
            borderRadius: '12px',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label={t('editCrypto.quantity')}
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ step: 'any', min: '0' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: androidColors.cardBackgroundLight,
                    '& fieldset': {
                      borderColor: androidColors.borderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: androidColors.primaryGold,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: androidColors.primaryGold,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: androidColors.textSecondary,
                    '&.Mui-focused': {
                      color: androidColors.primaryGold,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: androidColors.textColor,
                  },
                }}
              />

              <TextField
                label={t('editCrypto.buyPrice')}
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputProps={{ step: 'any', min: '0' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ color: androidColors.textSecondary }}>
                        €
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: androidColors.cardBackgroundLight,
                    '& fieldset': {
                      borderColor: androidColors.borderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: androidColors.primaryGold,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: androidColors.primaryGold,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: androidColors.textSecondary,
                    '&.Mui-focused': {
                      color: androidColors.primaryGold,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: androidColors.textColor,
                  },
                }}
              />

              {/* Informations actuelles */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: androidColors.cardBackgroundLight,
                  borderRadius: '8px',
                  border: `1px solid ${androidColors.borderColor}`,
                }}
              >
                <Typography variant="body2" sx={{ color: androidColors.textSecondary, mb: 1 }}>
                  {t('editCrypto.currentValues')}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ color: androidColors.textColor }}>
                    {t('editCrypto.currentQuantity')}
                  </Typography>
                  <Typography sx={{ color: androidColors.textColor, fontWeight: 'bold' }}>
                    {crypto.amount}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ color: androidColors.textColor }}>
                    {t('editCrypto.currentBuyPrice')}
                  </Typography>
                  <Typography sx={{ color: androidColors.textColor, fontWeight: 'bold' }}>
                    {crypto.price_buy.toFixed(2)} €
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: androidColors.textColor }}>
                    {t('editCrypto.totalValue')}
                  </Typography>
                  <Typography sx={{ color: androidColors.textColor, fontWeight: 'bold' }}>
                    {crypto.value_current.toFixed(2)} €
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={handleEditCrypto}
                disabled={!isFormValid() || isLoading}
                sx={{
                  backgroundColor: androidColors.primaryGold,
                  color: androidColors.darkBackground,
                  fontWeight: 'bold',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: androidColors.darkGold,
                  },
                  '&:disabled': {
                    backgroundColor: androidColors.textMuted,
                    color: androidColors.textSecondary,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: androidColors.darkBackground }} />
                ) : (
                  t('editCrypto.submit')
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default EditCrypto;